import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, User, Loader2, Circle, Search, Send, ArrowLeft, Users, Inbox,
  Smile, Paperclip, Image, Video, FileText, X, Download, AtSign, File
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons
const FILE_ICONS = {
  image: Image,
  video: Video,
  document: FileText,
  audio: FileText,
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function MessagesPage() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const token = localStorage.getItem('token');
  
  let currentUserId = null;
  if (token) {
    try {
      currentUserId = JSON.parse(atob(token.split('.')[1])).user_id;
    } catch (e) {
      // Token parse error - ignore
    }
  }

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.participant_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/social/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setConversations(await res.json());
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (partnerId) => {
    try {
      const res = await fetch(`${API_URL}/api/social/chat/${partnerId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const searchUsersForMention = useCallback(async (query) => {
    if (query.length < 2) {
      setMentionUsers([]);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/social/users/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setMentionUsers(await res.json());
      }
    } catch (error) {
      console.error('Failed to search users:', error);
    }
  }, [token]);

  useEffect(() => {
    if (mentionSearch) {
      const timer = setTimeout(() => searchUsersForMention(mentionSearch), 300);
      return () => clearTimeout(timer);
    }
  }, [mentionSearch, searchUsersForMention]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    
    // Check for @ mention trigger
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.substring(lastAtIndex + 1);
      const spaceIndex = afterAt.indexOf(' ');
      if (spaceIndex === -1 && afterAt.length > 0) {
        setMentionSearch(afterAt);
        setShowMentions(true);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAtIndex = newMessage.lastIndexOf('@');
    const beforeAt = newMessage.substring(0, lastAtIndex);
    setNewMessage(`${beforeAt}@${user.name} `);
    setSelectedMentions([...selectedMentions, user.id]);
    setShowMentions(false);
    setMentionSearch('');
    inputRef.current?.focus();
  };

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji.native);
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const uploadedFiles = [];
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${API_URL}/api/social/chat/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedFiles.push(data);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setAttachments([...attachments, ...uploadedFiles]);
    setUploading(false);
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && attachments.length === 0) || !selectedConversation) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/social/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: selectedConversation.participant_id,
          content: newMessage.trim(),
          attachments: attachments.length > 0 ? attachments : null,
          mentions: selectedMentions.length > 0 ? selectedMentions : null
        })
      });

      if (res.ok) {
        const message = await res.json();
        setMessages(prev => [...prev, message]);
        setNewMessage('');
        setAttachments([]);
        setSelectedMentions([]);
        loadConversations();
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread_count, 0);

  const renderAttachment = (attachment) => {
    const Icon = FILE_ICONS[attachment.type] || File;
    
    if (attachment.type === 'image') {
      return (
        <a href={attachment.url} target="_blank" rel="noopener noreferrer" className="block">
          <img 
            src={attachment.url} 
            alt={attachment.name} 
            className="max-w-[200px] max-h-[200px] rounded-lg object-cover hover:opacity-90 transition-opacity"
          />
        </a>
      );
    }
    
    if (attachment.type === 'video') {
      return (
        <video 
          src={attachment.url} 
          controls 
          className="max-w-[300px] rounded-lg"
        />
      );
    }
    
    return (
      <a 
        href={attachment.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center gap-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
      >
        <Icon className="w-8 h-8 text-cyan-400" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white truncate">{attachment.name}</p>
          <p className="text-xs text-gray-400">{formatFileSize(attachment.size)}</p>
        </div>
        <Download className="w-4 h-4 text-gray-400" />
      </a>
    );
  };

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-100px)] flex flex-col mt-4" data-testid="messages-page">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Messages</h1>
                <p className="text-sm text-gray-400">
                  {totalUnread > 0 ? `${totalUnread} unread` : 'Your conversations'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Conversations List */}
          <div className="w-80 border-r border-white/10 flex flex-col bg-[#0a0f1a]">
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search conversations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                />
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Inbox className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No conversations yet</p>
                  <p className="text-gray-500 text-xs mt-1">Start a conversation from a business profile</p>
                </div>
              ) : (
                conversations
                  .filter(c => c.participant_name?.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => setSelectedConversation(conv)}
                      className={`w-full flex items-center gap-3 p-4 hover:bg-white/5 transition-colors border-b border-white/5 ${
                        selectedConversation?.id === conv.id ? 'bg-white/10' : ''
                      }`}
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="relative">
                        {conv.participant_avatar ? (
                          <img 
                            src={conv.participant_avatar} 
                            alt={conv.participant_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                            <User className="w-6 h-6 text-white" />
                          </div>
                        )}
                        {conv.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full text-xs flex items-center justify-center text-white font-bold">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 text-left overflow-hidden">
                        <div className="flex items-center justify-between mb-1">
                          <p className={`font-medium truncate ${conv.unread_count > 0 ? 'text-white' : 'text-gray-300'}`}>
                            {conv.participant_name}
                          </p>
                          <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                            {formatTime(conv.last_message_at)}
                          </span>
                        </div>
                        <p className={`text-sm truncate ${conv.unread_count > 0 ? 'text-gray-300 font-medium' : 'text-gray-500'}`}>
                          {conv.last_message}
                        </p>
                      </div>
                    </button>
                  ))
              )}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 flex flex-col bg-[#0d1525]">
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-10 h-10 text-gray-600" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-1">Select a conversation</h3>
                  <p className="text-gray-400 text-sm">Choose from your existing conversations</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="px-6 py-4 border-b border-white/10 flex items-center gap-4">
                  <button 
                    onClick={() => setSelectedConversation(null)}
                    className="lg:hidden p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-400" />
                  </button>
                  {selectedConversation.participant_avatar ? (
                    <img 
                      src={selectedConversation.participant_avatar} 
                      alt={selectedConversation.participant_name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                      <User className="w-5 h-5 text-white" />
                    </div>
                  )}
                  <div>
                    <p className="font-medium text-white">{selectedConversation.participant_name}</p>
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <Circle className="w-2 h-2 fill-emerald-400 text-emerald-400" /> Online
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <Send className="w-12 h-12 mx-auto mb-2 text-gray-600" />
                        <p className="text-gray-400">No messages yet</p>
                        <p className="text-sm text-gray-500">Send the first message!</p>
                      </div>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <motion.div
                        key={msg.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                            msg.sender_id === currentUserId 
                              ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white' 
                              : 'bg-white/10 text-white'
                          }`}
                        >
                          {msg.content && (
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                          )}
                          {msg.attachments && msg.attachments.length > 0 && (
                            <div className="mt-2 space-y-2">
                              {msg.attachments.map((attachment, i) => (
                                <div key={i}>{renderAttachment(attachment)}</div>
                              ))}
                            </div>
                          )}
                          <p className={`text-xs mt-2 ${
                            msg.sender_id === currentUserId ? 'text-white/60' : 'text-gray-500'
                          }`}>
                            {formatTime(msg.created_at)}
                          </p>
                        </div>
                      </motion.div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div className="px-4 py-2 border-t border-white/10 flex gap-2 overflow-x-auto">
                    {attachments.map((file, index) => (
                      <div key={index} className="relative flex-shrink-0">
                        {file.type === 'image' ? (
                          <img src={file.url} alt={file.name} className="h-16 w-16 object-cover rounded-lg" />
                        ) : (
                          <div className="h-16 w-16 bg-white/10 rounded-lg flex items-center justify-center">
                            <FileText className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                        <button
                          onClick={() => removeAttachment(index)}
                          className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Input */}
                <form onSubmit={sendMessage} className="p-4 border-t border-white/10 relative">
                  {/* Emoji Picker */}
                  <AnimatePresence>
                    {showEmojiPicker && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute bottom-full left-4 mb-2 z-50"
                      >
                        <Picker
                          data={data}
                          onEmojiSelect={handleEmojiSelect}
                          theme="dark"
                          previewPosition="none"
                          skinTonePosition="none"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Mention Suggestions */}
                  <AnimatePresence>
                    {showMentions && mentionUsers.length > 0 && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className="absolute bottom-full left-4 mb-2 w-64 bg-[#1a2235] border border-white/10 rounded-xl shadow-xl overflow-hidden z-50"
                      >
                        <div className="p-2 border-b border-white/10">
                          <p className="text-xs text-gray-400">Mention someone</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {mentionUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => insertMention(user)}
                              className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors"
                            >
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                                  <User className="w-4 h-4 text-white" />
                                </div>
                              )}
                              <div className="text-left">
                                <p className="text-sm font-medium text-white">{user.name}</p>
                                <p className="text-xs text-gray-400 capitalize">{user.role}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="flex items-center gap-2">
                    {/* Emoji Button */}
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                      data-testid="emoji-btn"
                    >
                      <Smile className="w-5 h-5" />
                    </button>

                    {/* Attachment Button */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                      data-testid="attach-btn"
                    >
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Paperclip className="w-5 h-5" />
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    {/* Mention Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setNewMessage(prev => prev + '@');
                        inputRef.current?.focus();
                      }}
                      className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
                      data-testid="mention-btn"
                    >
                      <AtSign className="w-5 h-5" />
                    </button>

                    {/* Message Input */}
                    <Input
                      ref={inputRef}
                      value={newMessage}
                      onChange={handleInputChange}
                      placeholder="Type your message..."
                      className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500 py-6"
                      disabled={sending}
                      data-testid="message-input"
                    />

                    {/* Send Button */}
                    <Button
                      type="submit"
                      disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                      className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-6"
                      data-testid="send-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
